'use client';

import { moneyFormatter } from '@/components/girl/utils';
import attendantFrontend from '@bells/api/attendants/frontend';
import { cn } from '@bells/core/lib/utils';
import { generateRestClient } from '@bells/core/rest/react';
import {
  attendantTravelCostsCalculatorPrice,
  attendantTravelCostsCalculatorPriceNotice,
  attendantTravelCostsCalculatorTitle,
} from '@bells/i18n';
import type { FoundLocationReturn } from '@bells/schema/models/mapbox';
import { Card, CardContent, CardHeader } from '@bells/ui/components/card';
import { LocationSearchInput } from '@bells/ui/components/location-search-input';
import { Skeleton } from '@bells/ui/components/skeleton';

import { Calculator } from 'lucide-react';
import { useState } from 'react';

const systemDistanceClient = generateRestClient(attendantFrontend);

export default function TravelCostCalculator({ slug }: { slug?: string }) {
  const [address, setAddress] = useState<FoundLocationReturn | undefined>(
    undefined
  );

  const {
    data: travelDistanceData,
    isLoading: isLoadingCalculate,
    error,
  } = systemDistanceClient.travelCosts.useQuery({
    queryKey: [
      'resolve-coordinates',
      address?.lat ?? 0,
      address?.lng ?? 0,
      slug,
    ],
    queryData: {
      query: {
        lat: address?.lat ?? 0,
        lng: address?.lng ?? 0,
      },
      params: { slug: slug ?? '' },
    },
    enabled: address !== undefined && slug !== undefined,
    select: (df) => df.body,
    retry: false,
  });

  return (
    <Card className='m-0 w-full border-0 bg-transparent p-0 shadow-none'>
      <CardHeader className='px-0 pt-0'>
        <h3 className='flex items-center font-semibold leading-none tracking-tight'>
          <Calculator className='mr-1 size-4'></Calculator>
          {attendantTravelCostsCalculatorTitle()}
        </h3>
      </CardHeader>
      <CardContent className='flex w-full flex-col space-y-4 px-0'>
        <LocationSearchInput
          onResolve={setAddress}
          title={attendantTravelCostsCalculatorTitle()}
          disabled={isLoadingCalculate}
          query={{
            types: ['poi', 'postcode', 'place', 'street', 'address'],
          }}
        ></LocationSearchInput>
        {address && error && (
          <div className='w-full pt-2 font-bold text-destructive'>
            Fahrtkosten konnten nicht berechnet werden.
          </div>
        )}
        {address && !error && (
          <div className='grid flex-wrap items-center justify-between gap-4 lg:grid-cols-2'>
            <div className='flex w-full flex-wrap items-center gap-4'>
              <span className='font-bold'>
                {attendantTravelCostsCalculatorPrice()}
              </span>
              <div className='flex-1 font-bold underline'>
                <Skeleton
                  className={cn(
                    'hidden h-8 w-full',
                    !isLoadingCalculate ? 'hidden' : 'block'
                  )}
                ></Skeleton>
                {!isLoadingCalculate &&
                  travelDistanceData &&
                  moneyFormatter.format(travelDistanceData.costs)}
              </div>
            </div>
            <div className='w-full text-sm text-muted-foreground'>
              <Skeleton
                className={cn(
                  'hidden h-8 w-full',
                  !isLoadingCalculate ? 'hidden' : 'block'
                )}
              ></Skeleton>

              {!isLoadingCalculate &&
                travelDistanceData &&
                attendantTravelCostsCalculatorPriceNotice({
                  distance: `${travelDistanceData.distance} km`,
                })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
